import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { DataGridProProps, GridColDef, GridRenderCellParams, GridRowModel, GridSortModel } from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CopyIcon from '@mui/icons-material/FileCopyOutlined';
import { FC, MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { DateTime } from 'luxon';
import { DataList } from '../../components/DataList';
import SearchBar from '../../components/SearchBar';
import {
  AccountOrder,
  AccountOrderField,
  CompanyVerificationStatus,
  MutationStatusCode,
  OrderDirection,
  TenantAccount,
} from '../../gql-types.generated';
import { deleteTenant, fetchAllTenants } from './AllTenantsActions';
import { RoutePath } from '../../util/Routes';
import { verificationStatusConfig as statusConfig } from '../../components/VerificationStatusIndicator';
import {
  captureDeleteError,
  captureDeleteStatus,
  captureOrderBy,
  captureQueryString,
  captureStatusFilter,
  selectAllTenants,
  selectAllTenantsError,
  selectAllTenantsLoading,
  selectAllTenantsOrderBy,
  selectAllTenantsQueryString,
  selectAllTenantsStatusFilter,
  selectAllTenantsTotalCount,
  selectDeleteTenantError,
  selectDeleteTenantStatus,
  selectTenantsConnection,
} from './AllTenantsSlice';

const statusAll = 'ALL';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column nowrap',
      flex: 1,
      height: '100%',
      minHeight: 350,
    },
    headingSection: {
      padding: theme.spacing(1.5, 2.5),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    titleRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
    filterRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },
    searchWrapper: {
      flexGrow: 1,
      flexShrink: 1,
      minWidth: 200,
      maxWidth: 450,
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    statusSelect: {
      minWidth: 160,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
      },
    },
    statusCell: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      overflow: 'hidden',
      width: '100%',
    },
    statusDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
    },
    statusMenuDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'inline-block',
      marginRight: theme.spacing(1),
    },
    actionCell: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    viewButton: {
      textTransform: 'none',
    },
    clickableRow: {
      cursor: 'pointer',
    },
    companyNameCell: {
      fontWeight: 600,
    },
    dbaCell: {
      color: theme.palette.primary.main,
    },
    copyableCell: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      overflow: 'hidden',
      width: '100%',
      '&:hover $copyButton': {
        opacity: 1,
      },
    },
    truncatedText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    copyButton: {
      // Hidden via opacity rather than visibility/display: the button keeps its place in
      // the layout (so the id does not reflow on hover) and stays in the tab order for
      // keyboard users. The :focus rule is what makes it usable without a mouse.
      opacity: 0,
      transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
      '&:focus': {
        opacity: 1,
      },
    },
    copyIcon: {
      fontSize: '1rem',
    },
    deleteDialogPaper: {
      position: 'relative',
      width: 400,
      maxWidth: '100%',
      padding: theme.spacing(3),
    },
    deleteDialogHeader: {
      marginBottom: theme.spacing(1),
    },
    deleteDialogBody: {
      marginBottom: theme.spacing(2),
    },
    deleteDialogActions: {
      marginTop: theme.spacing(3),
    },
    deleteProgress: {
      color: theme.palette.primary.main,
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -25,
      marginLeft: -25,
    },
  }),
);

interface AllTenantsProps {
  onSelectTenantAccount: (tenantId: string) => void;
}

const AllTenants: FC<AllTenantsProps> = ({ onSelectTenantAccount }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const tenants = useSelector(selectAllTenants);
  const tenantsConnection = useSelector(selectTenantsConnection);
  const totalCount = useSelector(selectAllTenantsTotalCount);
  const orderBy = useSelector(selectAllTenantsOrderBy);
  const queryString = useSelector(selectAllTenantsQueryString);
  const statusFilter = useSelector(selectAllTenantsStatusFilter);
  const queryError = useSelector(selectAllTenantsError);
  const isLoading = useSelector(selectAllTenantsLoading);
  const deleteStatus = useSelector(selectDeleteTenantStatus);
  const deleteError = useSelector(selectDeleteTenantError);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantAccount | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Holds the value most recently copied, so only that cell shows the "Copied" tooltip.
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  useEffect(() => {
    setLoadingMore(false);
  }, [tenants]);

  useEffect(() => {
    if (deleteStatus?.code === MutationStatusCode.Success) {
      // Delete succeeded: close the dialog, clear status, and refresh the list from the first page.
      setIsDeleting(false);
      setTenantToDelete(null);
      dispatch(captureDeleteStatus(undefined));
      dispatch(fetchAllTenants(orderBy, undefined, queryString, statusFilter));
    } else if (deleteStatus?.code === MutationStatusCode.Error || deleteError) {
      // Keep the dialog open so the error is shown next to the actions.
      setIsDeleting(false);
    }
  }, [deleteStatus?.code, deleteError]);

  useEffect(() => {
    return () => {
      dispatch(captureOrderBy(undefined));
      dispatch(captureQueryString(undefined));
      dispatch(captureStatusFilter(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAllTenants(orderBy, undefined, queryString, statusFilter));
  }, [dispatch, orderBy, queryString, statusFilter]);

  const formatDate = (isoDate?: string | null): string => {
    if (!isoDate) return '—';
    const dt = DateTime.fromISO(isoDate);
    return dt.isValid ? dt.toFormat('MMM d, yyyy') : '—';
  };

  const getMid = (tenant: TenantAccount): string => {
    const ids = tenant.payfac?.ids;
    if (!ids || ids.length === 0) return '—';
    return ids[0]?.resourceId || '—';
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    if (model.length === 0) {
      dispatch(captureOrderBy(undefined));
      return;
    }
    const sort = model[0];
    if (sort.field === 'createdAt' && sort.sort) {
      const order: AccountOrder = {
        field: AccountOrderField.Timestamp,
        direction: sort.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc,
      };
      dispatch(captureOrderBy(order));
    } else {
      dispatch(captureOrderBy(undefined));
    }
  };

  const handleQueryStringChange = (value: string | undefined) => {
    dispatch(captureQueryString(value));
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    dispatch(captureStatusFilter(value === statusAll ? undefined : (value as CompanyVerificationStatus)));
  };

  const handleCopyValue = (event: MouseEvent<HTMLButtonElement>, value: string) => {
    // The grid's onRowClick selects the tenant. Without this, copying also navigates.
    event.stopPropagation();
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value);
    setCopiedValue(value);
  };

  // Truncated value with a full-value tooltip plus a copy button revealed on cell hover.
  // `label` feeds the aria-label; `cyPrefix` keys the data-cy hook.
  const renderCopyableCell = (params: GridRenderCellParams, label: string, cyPrefix: string) => {
    const value = params.value as string;
    if (value === '—') return <span>{value}</span>;
    const copied = copiedValue === value;
    return (
      <Box className={classes.copyableCell}>
        <Tooltip title={value} placement="top">
          <span className={classes.truncatedText}>{value}</span>
        </Tooltip>
        <Tooltip title={`${copied ? 'Copied' : 'Copy'} to clipboard`} placement="top">
          <IconButton
            size="small"
            className={classes.copyButton}
            aria-label={`Copy ${label} ${value} to clipboard`}
            onClick={event => handleCopyValue(event, value)}
            onMouseEnter={() => {
              if (copied) setCopiedValue(null);
            }}
            onFocus={() => {
              if (copied) setCopiedValue(null);
            }}
            data-cy={`copy-${cyPrefix}-${value}`}
          >
            <CopyIcon className={classes.copyIcon} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const loadMoreTenants = () => {
    if (!tenantsConnection?.pageInfo.hasNextPage) return;
    if (!tenantsConnection?.pageInfo.endCursor) return;
    setLoadingMore(true);
    dispatch(fetchAllTenants(orderBy, tenantsConnection.pageInfo.endCursor, queryString, statusFilter));
  };

  const openDeleteDialog = (tenant: TenantAccount) => {
    dispatch(captureDeleteError(undefined));
    setTenantToDelete(tenant);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setTenantToDelete(null);
    dispatch(captureDeleteError(undefined));
  };

  const confirmDeleteTenant = () => {
    const tenantId = tenantToDelete?.owner?.tenantId;
    if (!tenantId) return;
    setIsDeleting(true);
    dispatch(deleteTenant(tenantId));
  };

  const deleteDialogName = tenantToDelete?.company?.name || tenantToDelete?.businessProfile?.name || 'this tenant';

  const getRows = (): GridRowModel[] =>
    tenants.map((tenant: TenantAccount) => ({
      _raw: tenant,
      id: tenant.id,
      companyName: tenant.company?.name || '—',
      dba: tenant.businessProfile?.name || '—',
      status: tenant.company?.verification?.status ?? CompanyVerificationStatus.Unspecified,
      tenantId: tenant.owner?.tenantId || '—',
      mid: getMid(tenant),
      createdAt: tenant.createdAt ?? null,
      onboardedDate: tenant.tosAcceptance?.date ?? null,
      payFacType: tenant.payFacType || '—',
    }));

  const columns: GridColDef[] = [
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'companyName',
      headerName: 'Company Name',
      sortable: false,
      flex: 0.2,
      minWidth: 150,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string;
        if (value === '—') return <span>{value}</span>;
        return (
          <Tooltip title={value} placement="top">
            <span className={classes.companyNameCell} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'dba',
      headerName: 'Doing Business As (DBA)',
      sortable: false,
      flex: 0.2,
      minWidth: 160,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string;
        if (value === '—') return <span>{value}</span>;
        return (
          <Tooltip title={value} placement="top">
            <span className={classes.dbaCell} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 0.14,
      minWidth: 145,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as CompanyVerificationStatus;
        const config = statusConfig[status] ?? statusConfig[CompanyVerificationStatus.Unspecified];
        return (
          <Tooltip title={config.label} placement="top">
            <Box className={classes.statusCell}>
              <Box className={classes.statusDot} style={{ backgroundColor: config.color }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{config.label}</span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'tenantId',
      headerName: 'Tenant ID',
      sortable: false,
      flex: 0.14,
      // Wider than before so the value stays legible alongside the copy button.
      minWidth: 160,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => renderCopyableCell(params, 'tenant ID', 'tenant-id'),
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'mid',
      headerName: 'Merchant ID (MID)',
      sortable: false,
      flex: 0.14,
      // Wider than before so the value stays legible alongside the copy button.
      minWidth: 175,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => renderCopyableCell(params, 'merchant ID', 'mid'),
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'createdAt',
      headerName: 'Created Date',
      sortable: true,
      flex: 0.12,
      minWidth: 130,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => <span>{formatDate(params.value)}</span>,
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'onboardedDate',
      headerName: 'Onboarded Date',
      sortable: false,
      flex: 0.12,
      minWidth: 130,
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => <span>{formatDate(params.value)}</span>,
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'payFacType',
      headerName: 'PayFac Type',
      sortable: false,
      flex: 0.1,
      minWidth: 110,
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      sortable: false,
      field: 'actions',
      headerName: '',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      // eslint-disable-next-line react/display-name
      renderCell: (params: GridRenderCellParams) => {
        // eslint-disable-next-line no-underscore-dangle
        const tenant = params.row._raw as TenantAccount;
        return (
          <Box className={classes.actionCell}>
            <Button
              className={classes.viewButton}
              size="small"
              variant="outlined"
              onClick={e => {
                e.stopPropagation();
                if (tenant.owner?.tenantId) onSelectTenantAccount(tenant.owner.tenantId);
              }}
              data-cy={`view-tenant-${tenant.owner?.tenantId}`}
            >
              View
            </Button>
            <IconButton
              aria-label="Edit tenant"
              size="small"
              onClick={e => {
                e.stopPropagation();
                if (tenant.owner?.tenantId) {
                  history.push(RoutePath.TenantSettings.replace(':tenantId', tenant.owner.tenantId));
                }
              }}
              data-cy={`edit-tenant-${tenant.owner?.tenantId}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Delete tenant"
              size="small"
              onClick={e => {
                e.stopPropagation();
                openDeleteDialog(tenant);
              }}
              data-cy={`delete-tenant-${tenant.owner?.tenantId}`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const gridOptions: DataGridProProps = {
    rows: getRows(),
    'aria-label': 'All tenants table',
    columns,
    loading: loadingMore,
    sortingOrder: ['asc', 'desc'],
    sortModel,
    onSortModelChange: handleSortModelChange,
    onRowClick: params => {
      // eslint-disable-next-line no-underscore-dangle
      const tenant = params.row._raw as TenantAccount;
      if (tenant.owner?.tenantId) {
        onSelectTenantAccount(tenant.owner.tenantId);
      }
    },
    onRowsScrollEnd: queryError ? undefined : loadMoreTenants,
    getRowClassName: () => classes.clickableRow,
  };

  const selectedStatusValue = statusFilter && statusFilter.length > 0 ? statusFilter[0] : statusAll;

  return (
    <Paper className={classes.root} role="region" aria-label="All Tenants">
      <Helmet>
        <meta name="ai:viewId" content="all-tenants"></meta>
        <meta name="ai:viewDescription" content="Aptean Pay Merchant Portal - All Tenants"></meta>
        <title>Aptean Pay Merchant Portal - All Tenants</title>
      </Helmet>
      <Dialog
        aria-label="delete tenant dialog"
        maxWidth={false}
        open={!!tenantToDelete}
        onClose={closeDeleteDialog}
        disableScrollLock
        disableEscapeKeyDown={isDeleting}
        aria-labelledby="delete-tenant-title"
        aria-describedby="delete-tenant-description"
      >
        <Box className={classes.deleteDialogPaper}>
          <Typography
            variant="h6"
            className={classes.deleteDialogHeader}
            id="delete-tenant-title"
            data-cy="delete-tenant-dialog-header"
          >
            Delete tenant
          </Typography>
          <Typography variant="body1" className={classes.deleteDialogBody} id="delete-tenant-description">
            Are you sure you want to delete <strong>{deleteDialogName}</strong>? This soft-deletes the tenant account and all of its
            associated users. This action cannot be undone.
          </Typography>
          <DialogActions className={classes.deleteDialogActions}>
            <Button color="primary" onClick={closeDeleteDialog} disabled={isDeleting} data-cy="delete-tenant-cancel">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={confirmDeleteTenant}
              disabled={isDeleting}
              data-cy="delete-tenant-confirm"
            >
              DELETE
            </Button>
          </DialogActions>
          {isDeleting && <CircularProgress aria-label="progress spinner" size={50} className={classes.deleteProgress} />}
          {deleteError && (
            <FormHelperText error data-cy="delete-tenant-error">
              Unable to delete the tenant.
              <br />
              {deleteError.message}
            </FormHelperText>
          )}
        </Box>
      </Dialog>
      <Box className={classes.headingSection}>
        <Box className={classes.titleRow}>
          <Typography variant="title" data-cy="all-tenants-header">
            All merchants
          </Typography>
          <Typography variant="body2" color="textSecondary" data-cy="all-tenants-count">
            {totalCount} {totalCount === 1 ? 'tenant' : 'tenants'}
          </Typography>
        </Box>
        <Box className={classes.filterRow}>
          <Box className={classes.searchWrapper}>
            <SearchBar
              placeholderText="Search by company, DBA, tenant ID, email, or MID"
              queryString={queryString}
              updateQueryString={handleQueryStringChange}
              minimumCharacter={1}
              isSpacesAllowed
              searchName="all-tenants"
            />
          </Box>
          <FormControl size="small" className={classes.statusSelect}>
            <Select
              value={selectedStatusValue}
              onChange={handleStatusChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Filter by status', 'data-cy': 'all-tenants-status-filter' }}
            >
              <MenuItem value={statusAll}>All statuses</MenuItem>
              {Object.entries(statusConfig).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  <Box component="span" className={classes.statusMenuDot} style={{ backgroundColor: cfg.color }} />
                  {cfg.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Divider />
      <DataList
        gridOptions={gridOptions}
        isSkeletonHidden={!isLoading}
        cypressTag="all-tenants-table-body"
        noDataText="No tenants to show."
        sortingMode="server"
        hasQueryError={!!queryError}
      />
    </Paper>
  );
};

export default AllTenants;
